import React, { useState, useEffect } from 'react'
import {
  Table,
  Button,
  Space,
  Tag,
  Input,
  Modal,
  Form,
  message,
  Typography,
  Grid,
  Select,
  Card,
  Row,
  Col,
  Statistic,
  Tabs,
  Popconfirm,
  Transfer,
  Alert,
  Divider,
} from 'antd'
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  TeamOutlined,
  UserOutlined,
  SafetyCertificateOutlined,
  KeyOutlined,
  LockOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons'
import { roleApi, userApi } from '@/services/api'

const { Title, Text } = Typography
const { Search } = Input
const { useBreakpoint } = Grid
const { Option } = Select
const { TabPane } = Tabs

interface Role {
  id: string
  role_name: string
  role_code: string
  description: string
  is_active: boolean
  permissions: Permission[]
  created_at: string
  updated_at?: string
}

interface Permission {
  id: string
  permission_name: string
  permission_code: string
  resource_type: string
  action: string
  description: string
  created_at: string
}

interface User {
  id: string
  username: string
  email: string
  phone?: string
  full_name?: string
  avatar_url?: string
  role_id?: string
  role_name?: string
  status: 'active' | 'inactive'
  is_active: boolean
  is_verified: boolean
  created_at: string
  last_login_at?: string
  wechat_union_id?: string
  wechat_nickname?: string
  wechat_avatar_url?: string
  wechat_bound_at?: string
  login_methods?: string[]
}

const RoleManagement: React.FC = () => {
  const [roles, setRoles] = useState<Role[]>([])
  const [users, setUsers] = useState<User[]>([])
  const [permissions, setPermissions] = useState<Permission[]>([])
  const [loading, setLoading] = useState(false)
  const [searchText, setSearchText] = useState('')
  const [modalVisible, setModalVisible] = useState(false)
  const [editingRole, setEditingRole] = useState<Role | null>(null)
  const [form] = Form.useForm()
  const screens = useBreakpoint()
  const [activeTab, setActiveTab] = useState('roles')
  const [targetKeys, setTargetKeys] = useState<React.Key[]>([])
  // 分页状态
  const [rolePagination, setRolePagination] = useState({ current: 1, pageSize: 10, total: 0 })
  const [userPagination, setUserPagination] = useState({ current: 1, pageSize: 10, total: 0 })

  // 角色统计
  const [roleStats, setRoleStats] = useState({
    total: 0,
    systemRoles: 0,
    customRoles: 0,
    permissions: 0,
    activeUsers: 0,
    inactiveUsers: 0,
  })

  useEffect(() => {
    fetchRoles()
    fetchUsers()
    fetchPermissions()
  }, [searchText, rolePagination.current, userPagination.current])

  const fetchRoles = async () => {
    setLoading(true)
    try {
      const response = await roleApi.getRoles({
        page: rolePagination.current,
        page_size: rolePagination.pageSize,
      })
      const items = response.data.items || []
      const total = response.data.total || 0
      setRoles(items)
      setRolePagination(prev => ({ ...prev, total }))

      // 更新统计（基于当前页数据，无法获取真实全局统计）
      setRoleStats(prev => ({
        ...prev,
        total: total,
        systemRoles: items.filter(r => r.is_system).length,
        customRoles: items.filter(r => !r.is_system).length,
      }))
    } catch (error) {
      message.error('获取角色列表失败')
    } finally {
      setLoading(false)
    }
  }

  const fetchUsers = async () => {
    try {
      const response = await userApi.getUsers({
        page: userPagination.current,
        page_size: userPagination.pageSize,
        search: searchText || undefined,
      })
      const items = response.data.items || []
      const total = response.data.total || 0
      setUsers(items)
      setUserPagination(prev => ({ ...prev, total }))

      // 更新用户统计（基于当前页数据）
      setRoleStats(prev => ({
        ...prev,
        activeUsers: items.filter(u => u.status === 'active').length,
        inactiveUsers: items.filter(u => u.status === 'inactive').length,
      }))
    } catch (error) {
      console.error('Failed to fetch users:', error)
    }
  }

  const fetchPermissions = async () => {
    try {
      const response = await roleApi.getPermissions({
        page: 1,
        page_size: 200,
      })
      setPermissions(response.data.items || [])
    } catch (error) {
      console.error('Failed to fetch permissions:', error)
    }
  }

  const handleCreateRole = async (values: any) => {
    try {
      const roleData = {
        role_name: values.role_name,
        role_code: values.role_code,
        description: values.description,
        permission_ids: targetKeys,
      }
      await roleApi.createRole(roleData)
      message.success('创建角色成功')
      setModalVisible(false)
      form.resetFields()
      setTargetKeys([])
      fetchRoles()
    } catch (error: any) {
      message.error(error.response?.data?.detail || '创建角色失败')
    }
  }

  const handleUpdateRole = async (id: string, values: any) => {
    try {
      const roleData = {
        role_name: values.role_name,
        role_code: values.role_code,
        description: values.description,
        permission_ids: targetKeys,
      }
      await roleApi.updateRole(id, roleData)
      message.success('更新角色成功')
      setModalVisible(false)
      setEditingRole(null)
      form.resetFields()
      setTargetKeys([])
      fetchRoles()
    } catch (error: any) {
      message.error(error.response?.data?.detail || '更新角色失败')
    }
  }

  const handleDeleteRole = async (id: string) => {
    try {
      await roleApi.deleteRole(id)
      message.success('删除角色成功')
      fetchRoles()
    } catch (error) {
      message.error('删除角色失败')
    }
  }

  const handleUpdateUserRole = async (userId: string, roleId: string) => {
    try {
      await roleApi.updateUserRole(userId, { role_id: roleId })
      message.success('更新用户角色成功')
      fetchUsers()
    } catch (error) {
      message.error('更新用户角色失败')
    }
  }

  const roleColumns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
      render: (id: string) => <Text copyable>{id}</Text>
    },
    {
      title: '角色名称',
      dataIndex: 'role_name',
      key: 'role_name',
      render: (text: string, record: Role) => (
        <Space>
          <TeamOutlined className="text-blue-500" />
          <div>
            <div className="font-medium">{text}</div>
            <div className="text-xs text-gray-500">{record.description}</div>
          </div>
        </Space>
      ),
    },
    {
      title: '角色代码',
      dataIndex: 'role_code',
      key: 'role_code',
      render: (code: string) => (
        <Tag color="blue">{code}</Tag>
      ),
    },
    {
      title: '状态',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? '启用' : '禁用'}
        </Tag>
      ),
    },
    {
      title: '权限数量',
      key: 'permission_count',
      render: (_, record: Role) => (
        <Space>
          <KeyOutlined />
          <Text>{record.permissions?.length || 0}</Text>
        </Space>
      ),
    },
    {
      title: '操作',
      key: 'actions',
      render: (_, record: Role) => (
        <Space>
          <Button
            type="text"
            icon={<SafetyCertificateOutlined />}
            onClick={() => {
              setEditingRole(record)
              form.setFieldsValue({
                role_name: record.role_name,
                role_code: record.role_code,
                description: record.description,
              })
              setTargetKeys(record.permissions?.map((p) => p.id) || [])
              setModalVisible(true)
            }}
          >
            权限
          </Button>
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => {
              setEditingRole(record)
              form.setFieldsValue({
                role_name: record.role_name,
                role_code: record.role_code,
                description: record.description,
              })
              setTargetKeys(record.permissions?.map((p) => p.id) || [])
              setModalVisible(true)
            }}
          >
            编辑
          </Button>
          <Popconfirm
            title="确定要删除这个角色吗？"
            onConfirm={() => handleDeleteRole(record.id)}
          >
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
            >
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ]

  const userColumns = [
    {
      title: '用户信息',
      dataIndex: 'username',
      key: 'username',
      render: (text: string, record: User) => (
        <Space>
          {record.avatar_url ? (
            <img src={record.avatar_url} alt="avatar" className="w-8 h-8 rounded-full" />
          ) : (
            <UserOutlined />
          )}
          <div>
            <div className="font-medium">{text}</div>
            <div className="text-xs text-gray-500">{record.email}</div>
            {record.phone && <div className="text-xs text-gray-500">{record.phone}</div>}
          </div>
        </Space>
      ),
    },
    {
      title: '登录方式',
      key: 'login_methods',
      render: (_, record: User) => (
        <Space>
          {record.email && <Tag color="blue">邮箱</Tag>}
          {record.phone && <Tag color="green">手机</Tag>}
          {record.wechat_union_id && <Tag color="success">微信</Tag>}
        </Space>
      ),
    },
    {
      title: '角色',
      dataIndex: 'role_name',
      key: 'role_name',
      width: 180,
      render: (roleName: string, record: User) => {
        // 从 role_code 提取前缀
        const getRolePrefix = (code: string) => {
          const prefixMap: Record<string, string> = {
            super_admin: 'admin',
            platform_admin: 'admin',
            admin: 'admin',
            merchant_admin: 'admin',
            venue_admin: 'admin',
            merchant: 'merchant',
            playwright: 'content',
            reviewer: 'content',
            actor: 'actor',
            advertiser: 'ads',
            agent_developer: 'dev',
            player: 'user',
          }
          return prefixMap[code] || code.split('_')[0] || 'unknown'
        }

        return (
          <Select
            value={record.role_id}
            style={{ width: 170 }}
            onChange={(value) => handleUpdateUserRole(record.id, value)}
            placeholder="选择角色"
            optionLabelProp="label"
          >
            {roles.map((role) => {
              const prefix = getRolePrefix(role.role_code)
              return (
                <Option
                  key={role.id}
                  value={role.id}
                  label={`[${prefix}] ${role.role_name}`}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Tag color="blue" size="small">{prefix}</Tag>
                    <span>{role.role_name}</span>
                  </div>
                </Option>
              )
            })}
          </Select>
        )
      },
    },
    {
      title: '状态',
      key: 'status',
      render: (_, record: User) => (
        <Space direction="vertical" size={0}>
          <Tag color={record.is_active ? 'success' : 'default'}>
            {record.is_active ? '活跃' : '禁用'}
          </Tag>
          {record.is_verified && <Tag color="blue">已验证</Tag>}
        </Space>
      ),
    },
    {
      title: '微信绑定',
      key: 'wechat',
      render: (_, record: User) => (
        record.wechat_union_id ? (
          <Space>
            {record.wechat_avatar_url && (
              <img src={record.wechat_avatar_url} alt="wechat" className="w-6 h-6 rounded-full" />
            )}
            <Text>{record.wechat_nickname || '已绑定'}</Text>
          </Space>
        ) : (
          <Text type="secondary">未绑定</Text>
        )
      ),
    },
    {
      title: '最后登录',
      dataIndex: 'last_login_at',
      key: 'last_login_at',
      render: (date?: string) => date ? new Date(date).toLocaleString('zh-CN') : '从未',
    },
    {
      title: '注册时间',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string) => new Date(date).toLocaleDateString('zh-CN'),
    },
  ]

  const permissionData = permissions.map((p) => ({
    key: p.id,
    title: `${p.resource_type}:${p.action}`,
    description: p.description || p.permission_name,
  }))

  return (
    <div className="p-6">
      <div className="mb-6">
        <Title level={2}>角色权限管理</Title>
        <Text type="secondary">管理系统角色、权限和用户访问控制</Text>
      </div>

      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        <TabPane tab="角色管理" key="roles">
          <Card>
            <div className="mb-4 flex justify-between items-center">
              <Space>
                <Search
                  placeholder="搜索角色"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  style={{ width: 300 }}
                  allowClear
                />
              </Space>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => {
                  setEditingRole(null)
                  form.resetFields()
                  setTargetKeys([])
                  setModalVisible(true)
                }}
              >
                创建角色
              </Button>
            </div>

            <Table
              columns={roleColumns}
              dataSource={roles}
              rowKey="id"
              loading={loading}
              pagination={{
                current: rolePagination.current,
                pageSize: rolePagination.pageSize,
                total: rolePagination.total,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total) => `共 ${total} 条记录`,
                onChange: (page, pageSize) => {
                  setRolePagination(prev => ({ current: page, pageSize: pageSize || 10, total: prev.total }))
                },
              }}
            />
          </Card>
        </TabPane>

        <TabPane tab="用户管理" key="users">
          <Card>
            <div className="mb-4">
              <Search
                placeholder="搜索用户"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                style={{ width: 300 }}
                allowClear
              />
            </div>

            <Table
              columns={userColumns}
              dataSource={users}
              rowKey="id"
              loading={loading}
              pagination={{
                current: userPagination.current,
                pageSize: userPagination.pageSize,
                total: userPagination.total,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total) => `共 ${total} 条记录`,
                onChange: (page, pageSize) => {
                  setUserPagination(prev => ({ current: page, pageSize: pageSize || 10, total: prev.total }))
                },
              }}
            />
          </Card>
        </TabPane>

        <TabPane tab="权限列表" key="permissions">
          <Card>
            <div className="text-center py-12">
              <KeyOutlined className="text-6xl text-gray-300 mb-4" />
              <Title level={3}>权限列表</Title>
              <Text type="secondary">查看所有可用的权限</Text>
            </div>
            <div className="mt-4">
              <Row gutter={16}>
                {['用户管理', '角色管理', 'AI助理', '通知管理', '广告管理', '文件管理', '剧本管理', '场馆管理'].map((resource) => (
                  <Col span={6} key={resource}>
                    <Card size="small" title={resource}>
                      <Space direction="vertical" style={{ width: '100%' }}>
                        <Tag>查看</Tag>
                        <Tag>创建</Tag>
                        <Tag>编辑</Tag>
                        <Tag>删除</Tag>
                      </Space>
                    </Card>
                  </Col>
                ))}
              </Row>
            </div>
          </Card>
        </TabPane>

        <TabPane tab="访问日志" key="logs">
          <Card>
            <Alert
              message="访问控制"
              description="查看用户权限变更和访问日志"
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />
            <div className="text-center py-12">
              <LockOutlined className="text-6xl text-gray-300 mb-4" />
              <Title level={3}>访问日志</Title>
              <Text type="secondary">查看权限变更和用户访问记录</Text>
            </div>
          </Card>
        </TabPane>

        <TabPane tab="统计" key="stats">
          <Card>
            <Row gutter={16}>
              <Col span={6}>
                <Statistic
                  title="总角色数"
                  value={roleStats.total}
                  prefix={<TeamOutlined />}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title="系统角色"
                  value={roleStats.systemRoles}
                  prefix={<SafetyCertificateOutlined />}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title="自定义角色"
                  value={roleStats.customRoles}
                  prefix={<UserOutlined />}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title="总权限数"
                  value={permissions.length}
                  prefix={<KeyOutlined />}
                />
              </Col>
            </Row>
            <Divider />
            <Row gutter={16}>
              <Col span={6}>
                <Statistic
                  title="活跃用户"
                  value={roleStats.activeUsers}
                  prefix={<CheckCircleOutlined />}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title="非活跃用户"
                  value={roleStats.inactiveUsers}
                  prefix={<CloseCircleOutlined />}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title="最高权限级别"
                  value={Math.max(...roles.map(r => r.level), 0)}
                  prefix={<ExclamationCircleOutlined />}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title="平均用户/角色"
                  value={
                    roleStats.total
                      ? (users.length / roleStats.total).toFixed(1)
                      : 0
                  }
                />
              </Col>
            </Row>
          </Card>
        </TabPane>
      </Tabs>

      {/* 创建/编辑角色模态框 */}
      <Modal
        title={
          <span>
            {editingRole ? '编辑角色' : '创建角色'}
            {editingRole && (
              <Text type="secondary" className="ml-3 text-sm">
                ID: {editingRole.id}
              </Text>
            )}
          </span>
        }
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false)
          setEditingRole(null)
          form.resetFields()
          setTargetKeys([])
        }}
        footer={null}
        width={700}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={(values) => {
            if (editingRole) {
              handleUpdateRole(editingRole.id, values)
            } else {
              handleCreateRole(values)
            }
          }}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="role_name"
                label="角色名称"
                rules={[{ required: true, message: '请输入角色名称' }]}
              >
                <Input placeholder="例如：系统管理员" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="role_code"
                label="角色代码"
                rules={[
                  { required: true, message: '请输入角色代码' },
                  { pattern: /^[a-z_]+$/, message: '只能包含小写字母和下划线' }
                ]}
              >
                <Input placeholder="例如：system_admin" disabled={!!editingRole} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="description" label="角色描述">
            <Input.TextArea placeholder="请输入角色描述" rows={3} />
          </Form.Item>

          <Form.Item label="权限配置">
            <Transfer
              dataSource={permissionData}
              targetKeys={targetKeys}
              selectedKeys={[]}
              onChange={setTargetKeys}
              render={(item) => item.title}
              listStyle={{
                width: 300,
                height: 300,
              }}
              titles={['可用权限', '已选权限']}
            />
          </Form.Item>

          <Form.Item>
            <Space className="w-full justify-end">
              <Button
                onClick={() => {
                  setModalVisible(false)
                  setEditingRole(null)
                  form.resetFields()
                  setTargetKeys([])
                }}
              >
                取消
              </Button>
              <Button type="primary" htmlType="submit">
                {editingRole ? '更新' : '创建'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}

export default RoleManagement
